import {
    selectTradeDatesState,
    TradeDate,
} from 'tradeDates/store/tradeDatesSlice';
import { SymbolEvent } from 'widgets/corpActions/data/SymbolEvent';
import { useAppSelector } from 'store/hooks';
import { useMemo } from 'react';
import { isFriday, oneWeek, today } from 'utils/dates';
import orderBy from 'lodash/orderBy';
import { useServices } from 'app/hooks.ts';
import { useSelectAppSettings } from 'app/store/appSettings/selectors.ts';
import cloneDeep from 'lodash/cloneDeep';

export type EnrichedSymbolEvent = SymbolEvent & {
    isTminus1AftMftEarnings?: boolean;
};

export const isAftMktEarningsRelease = (ev: SymbolEvent) =>
    ev.type === 'EARN_RLS' && ev.expectedRptTime === 'Aft-mkt';

export const isTMinus1 = (
    date: number | undefined,
    previous7DaysTradeDates: TradeDate[] | undefined
) => {
    if (!previous7DaysTradeDates || previous7DaysTradeDates?.length === 0) {
        return isFriday(date);
    }

    const orderedTradingDates = orderBy(
        previous7DaysTradeDates,
        ['index'],
        ['desc']
    );

    const tMinus1 = orderedTradingDates[0];
    return date === tMinus1.date;
};

const noTradingDays: TradeDate[] = [];

/*
 * Returns a dictionary of the previous 7 days trade dates for the provided symbols (not including today)
 */
export const usePrevious7DaysTradeDates: (
    symbols: Array<string | undefined>
) => {
    [symbol: string]: TradeDate[];
} = (symbols) => {
    const tradeDatesState = useAppSelector(selectTradeDatesState);

    return useMemo(() => {
        const ret: {
            [symbol: string]: TradeDate[];
        } = {};
        const aWeekAgo = today() - oneWeek;
        return symbols.reduce((i, symbol) => {
            if (symbol) {
                const symbolTradeDates =
                    tradeDatesState[symbol] ?? noTradingDays;
                i[symbol] = symbolTradeDates.filter(
                    (d) => d.date < today() && d.date >= aWeekAgo
                );
            }
            return i;
        }, ret);
    }, [tradeDatesState, symbols]);
};

export const useEarningsAftMktEnricher: (events: SymbolEvent[]) => {
    enrichedSymbolEvents: Array<EnrichedSymbolEvent>;
} = (events) => {
    const { symbology } = useSelectAppSettings();
    const symbols = useMemo(() => events.map((s) => s.symbol), [events]);
    const lastWeeksTradeDates = usePrevious7DaysTradeDates(symbols);
    const { tradeDatesService } = useServices();

    const enrichedSymbolEvents = useMemo(() => {
        let enriched = false;
        const enrichedEvents: EnrichedSymbolEvent[] = cloneDeep(events);
        for (const ev of enrichedEvents) {
            if (ev.symbol && isAftMktEarningsRelease(ev)) {
                /*
                 * Ask the service to fetch the trade dates...
                 */
                tradeDatesService.requestTradeDates(ev.symbol, symbology, 7, 0);
                // Set the flag
                enriched = true;
                ev.isTminus1AftMftEarnings = isTMinus1(
                    ev.effectiveDate,
                    lastWeeksTradeDates[ev.symbol]
                );
            }
        }
        return enriched ? enrichedEvents : events;
    }, [events, tradeDatesService, lastWeeksTradeDates, symbology]);

    return { enrichedSymbolEvents };
};
